import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/api";
import { PageTitle } from "@/components/ui-kit";
import { Delta } from "@/pages/MySchools";
import { downloadFile } from "@/lib/download";
import { Camera, FileDown, School, UsersRound, CalendarDays } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";

const ST = { green: ["منجزة", "text-emerald-700"], yellow: ["متوسطة", "text-amber-700"], red: ["متأخرة", "text-red-700"] };

const SegCard = ({ title, s, tid }) => (
  <div data-testid={tid} className="rounded-2xl bg-white/85 border border-sky-200 p-4">
    <div className="font-semibold text-stone-800 mb-2">{title}</div>
    <div className="grid grid-cols-3 gap-2 text-center text-xs">
      <div><div className="font-bold text-sky-800 text-base">{s.schools}</div>مدرسة</div>
      <div><div className="font-bold text-sky-800 text-base">{s.avg_rate}%</div>متوسط المشاركة</div>
      <div><div className="font-bold text-sky-800 text-base">{s.hours}</div>ساعة</div>
      <div><div className="font-bold text-sky-800 text-base">{s.executed_opportunities}</div>فرصة منفذة</div>
      <div><div className="font-bold text-sky-800 text-base">{s.volunteers}</div>متطوع</div>
      <div><div className="font-bold text-emerald-700 text-base">{s.activated}</div>مفعّلة</div>
    </div>
    <div className="flex gap-3 justify-center mt-3 text-[11px]">
      <span className="text-emerald-700">● منجزة {s.green}</span><span className="text-amber-700">● متوسطة {s.yellow}</span><span className="text-red-700">● متأخرة {s.red}</span>
    </div>
  </div>
);

const SchoolsSection = ({ snap }) => {
  const [seg, setSeg] = useState("all");
  const [st, setSt] = useState("all");
  const rows = useMemo(() => snap.schools.filter((s) => (seg === "all" || s.type === seg) && (st === "all" || s.status === st))
    .sort((a, b) => b.participation_rate - a.participation_rate), [snap, seg, st]);
  const chart = snap.leaderboard.map((s) => ({ name: s.name.length > 22 ? `${s.name.slice(0, 22)}…` : s.name, v: s.executed_opportunities, h: s.hours }));
  const chip = (cur, set, k, l, tid) => <button key={k} data-testid={tid} onClick={() => set(k)} className={`px-3 py-1 rounded-full text-xs border ${cur === k ? "bg-sky-600 text-white border-sky-600" : "bg-white border-sky-300/50"}`}>{l}</button>;
  return (
    <section className="space-y-4" data-testid="snap-schools">
      <h3 className="font-head font-bold text-lg text-stone-800 flex items-center gap-2"><School size={20} className="text-sky-600" /> القسم الأول: متابعة المدارس بالشرائح وتقدّمها</h3>
      <div className="grid md:grid-cols-3 gap-3">
        <SegCard tid="seg-all" title="جميع المدارس" s={snap.segments.all} />
        <SegCard tid="seg-boys" title="مدارس البنين" s={snap.segments["بنين"]} />
        <SegCard tid="seg-girls" title="مدارس البنات" s={snap.segments["بنات"]} />
      </div>
      <div className="rounded-2xl bg-white/85 border border-amber-300/60 p-4" data-testid="snap-leaderboard">
        <div className="font-semibold text-stone-800 mb-2">لوحة الصدارة — أعلى 10 مدارس بالفرص المنفذة</div>
        <div style={{ height: Math.max(220, chart.length * 34) }} dir="ltr">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={170} orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, n) => [v, n === "v" ? "فرص منفذة" : "ساعات"]} />
              <Bar dataKey="v" fill="#d4a017" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {[["all", "كل الشرائح"], ["بنين", "بنين"], ["بنات", "بنات"]].map(([k, l]) => chip(seg, setSeg, k, l, `snap-seg-${k}`))}
        <span className="w-3" />
        {[["all", "كل الحالات"], ["red", "متأخرة"], ["yellow", "متوسطة"], ["green", "منجزة"]].map(([k, l]) => chip(st, setSt, k, l, `snap-st-${k}`))}
      </div>
      <div className="overflow-x-auto rounded-2xl bg-white/85 border border-sky-100 max-h-[480px]">
        <table className="w-full text-sm" data-testid="snap-schools-table">
          <thead className="sticky top-0 bg-sky-50"><tr className="text-stone-500 text-right">
            {["المدرسة", "الشريحة", "الحالة", "المشاركة", "التغير", "الساعات", "التغير", "الفرص المنفذة"].map((h, i) => <th key={i} className="py-2 px-2 font-medium whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>{rows.map((s) => (
            <tr key={s.id} className="border-t border-sky-100">
              <td className="py-1.5 px-2 text-stone-800">{s.name}</td><td className="px-2 text-xs">{s.type}</td>
              <td className={`px-2 text-xs ${ST[s.status][1]}`}>{ST[s.status][0]}</td>
              <td className="px-2">{s.participation_rate}%</td><td className="px-2"><Delta v={s.delta_rate} unit="%" /></td>
              <td className="px-2">{s.hours}</td><td className="px-2"><Delta v={s.delta_hours} /></td><td className="px-2">{s.executed_opportunities}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const SupervisorsSection = ({ sups }) => (
  <section className="space-y-3" data-testid="snap-supervisors">
    <h3 className="font-head font-bold text-lg text-stone-800 flex items-center gap-2"><UsersRound size={20} className="text-amber-600" /> القسم الثاني: متابعة المشرفين وإنجازهم</h3>
    <div className="overflow-x-auto rounded-2xl bg-white/85 border border-amber-200">
      <table className="w-full text-sm" data-testid="snap-sup-table">
        <thead className="bg-amber-50"><tr className="text-stone-500 text-right">
          {["#", "المشرف", "المدارس", "متوسط المشاركة", "التغير", "الساعات", "التغير", "فرص منفذة", "متأخرة", "المتابعات", "خطط مفتوحة/مغلقة"].map((h, i) => <th key={i} className="py-2 px-2 font-medium whitespace-nowrap">{h}</th>)}
        </tr></thead>
        <tbody>{[...sups].sort((a, b) => b.avg_rate - a.avg_rate || b.hours - a.hours).map((s, i) => (
          <tr key={s.id} className="border-t border-amber-100">
            <td className="py-1.5 px-2 font-bold text-amber-700">{i + 1}</td><td className="px-2 text-stone-800 whitespace-nowrap">{s.name}</td><td className="px-2">{s.schools}</td>
            <td className="px-2">{s.avg_rate}%</td><td className="px-2"><Delta v={s.delta_rate} unit="%" /></td>
            <td className="px-2">{s.hours}</td><td className="px-2"><Delta v={s.delta_hours} /></td><td className="px-2">{s.executed_opportunities}</td>
            <td className="px-2 text-red-700">{s.red}</td><td className="px-2">{s.visits}</td><td className="px-2">{s.open_plans}/{s.closed_plans}</td>
          </tr>))}
          {!sups.length && <tr><td colSpan={11} className="text-center py-4 text-stone-500">لا يوجد مشرفون</td></tr>}
        </tbody>
      </table>
    </div>
  </section>
);

export default function Snapshots() {
  const [list, setList] = useState([]);
  const [snap, setSnap] = useState(null);
  const [taking, setTaking] = useState(false);

  const open = (id) => api.get(`/snapshots/${id}`).then((r) => setSnap(r.data)).catch((e) => toast.error(apiError(e)));
  const loadList = (selectId) => api.get("/snapshots").then((r) => {
    setList(r.data);
    const id = selectId || r.data[0]?.id;
    if (id) open(id);
  }).catch((e) => toast.error(apiError(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadList(); }, []);

  const takeNow = async () => {
    setTaking(true);
    try { const r = await api.post("/snapshots/take"); toast.success(`تم التقاط لقطة ${r.data.date}`); loadList(r.data.id); }
    catch (e) { toast.error(apiError(e)); }
    finally { setTaking(false); }
  };

  const exportPdf = async () => {
    try { await downloadFile(`/snapshots/${snap.id}/pdf`, `لقطة-${snap.date}.pdf`); }
    catch { toast.error("تعذّر تصدير اللقطة"); }
  };

  return (
    <div className="space-y-5" data-testid="snapshots-page">
      <PageTitle title="اللقطات المعلوماتية" subtitle="أرشيف فريق المتابعة — يُحفظ تلقائياً كل اثنين وجمعة الساعة 6 صباحاً" icon={Camera}
        actions={<div className="flex gap-2 print:hidden">
          <button data-testid="snap-take" onClick={takeNow} disabled={taking} className="btn-gold px-4 py-2 rounded-xl text-sm flex items-center gap-1 disabled:opacity-60"><Camera size={15} /> {taking ? "جارٍ الالتقاط..." : "التقاط لقطة الآن"}</button>
          {snap && <button data-testid="snap-export-pdf" onClick={exportPdf} className="px-4 py-2 rounded-xl text-sm border border-sky-400/50 text-sky-800 bg-sky-50 flex items-center gap-1"><FileDown size={15} /> تصدير PDF</button>}
        </div>} />
      <div className="flex gap-2 overflow-x-auto pb-1 print:hidden" data-testid="snap-list">
        {list.map((s) => (
          <button key={s.id} data-testid={`snap-item-${s.date}`} onClick={() => open(s.id)}
            className={`shrink-0 px-3 py-2 rounded-xl border text-xs text-right ${snap?.id === s.id ? "bg-sky-600 text-white border-sky-600" : "bg-white border-sky-200 text-stone-700"}`}>
            <div className="font-semibold flex items-center gap-1"><CalendarDays size={12} /> {s.weekday} {s.date}</div>
            <div className="opacity-80">{s.source === "auto" ? "تلقائية" : "يدوية"} · مشاركة {s.segments?.all?.avg_rate ?? 0}%</div>
          </button>
        ))}
        {!list.length && <div className="text-sm text-stone-500 py-3" data-testid="snap-empty">لا توجد لقطات بعد — ستُلتقط تلقائياً كل اثنين وجمعة، أو اضغط «التقاط لقطة الآن».</div>}
      </div>
      {snap && (
        <div className="space-y-8" data-testid="snap-view">
          <div className="text-sm text-stone-600">لقطة يوم <b>{snap.weekday} {snap.date}</b> — التغيّرات مقارنة باللقطة السابقة</div>
          <SchoolsSection snap={snap} />
          <SupervisorsSection sups={snap.supervisors || []} />
        </div>
      )}
    </div>
  );
}
